<template>
  <div class="ops">
    <SectionHeader section-key="automation">
      <template #actions>
        <el-tag type="info" effect="plain">授权：建议确认</el-tag>
        <el-button :loading="loadingMeta" @click="refreshAll">刷新</el-button>
      </template>
    </SectionHeader>

    <div v-if="overview" class="overview-strip">
      <div class="ov-item"><span>策略</span><b>{{ overview.counts?.active_strategies ?? 0 }}</b></div>
      <div class="ov-item"><span>定时任务</span><b>{{ overview.counts?.enabled_tasks ?? 0 }}</b></div>
      <div class="ov-item"><span>7日调研</span><b>{{ overview.counts?.runs_7d ?? 0 }}</b></div>
      <div class="ov-item"><span>日历草稿</span><b>{{ overview.counts?.calendar_draft ?? 0 }}</b></div>
      <div class="ov-item"><span>待入队</span><b>{{ overview.counts?.calendar_approved ?? 0 }}</b></div>
      <div class="ov-item"><span>待确认建议</span><b>{{ overview.counts?.proposals_pending ?? 0 }}</b></div>
      <div class="ov-item wide" v-if="(overview.attention || []).length">
        <span>关注</span><b>{{ overview.attention.join(' · ') }}</b>
      </div>
    </div>

    <el-row :gutter="16">
      <el-col :xs="24" :lg="10">
        <el-card shadow="never" class="panel">
          <template #header>
            <div class="card-h">启动黄金流程</div>
          </template>
          <el-form label-position="top" @submit.prevent="runFullCycle">
            <el-form-item label="主账号平台">
              <el-select v-model="form.platform" style="width: 100%" @change="onPlatformChange">
                <el-option label="小红书" value="xiaohongshu" />
                <el-option label="抖音" value="douyin" />
              </el-select>
            </el-form-item>
            <el-form-item label="运营账号">
              <el-select v-model="form.account_id" filterable placeholder="选择账号" style="width: 100%">
                <el-option
                  v-for="a in accounts"
                  :key="a.account_id"
                  :label="`${a.display_name || a.account_id}`"
                  :value="a.account_id"
                />
              </el-select>
            </el-form-item>
            <el-form-item label="搜索账号（小红书 PC / 抖音，可与运营账号相同）">
              <el-input v-model="form.search_account_id" placeholder="默认与运营账号相同" />
            </el-form-item>
            <el-form-item label="业务目标">
              <el-input
                v-model="form.goal_text"
                type="textarea"
                :rows="4"
                placeholder="例：我卖适合小户型的智能家居，想做小红书和抖音，每月获得 300 个咨询"
              />
            </el-form-item>
            <el-form-item label="关键词（可选，逗号分隔；留空则自动扩展）">
              <el-input v-model="form.keywordsText" placeholder="小户型智能家居, 租房改造, 智能家居避坑" />
            </el-form-item>
            <el-form-item label="研究平台">
              <el-checkbox-group v-model="form.research_platforms">
                <el-checkbox label="xiaohongshu">小红书</el-checkbox>
                <el-checkbox label="douyin">抖音</el-checkbox>
              </el-checkbox-group>
            </el-form-item>
            <el-form-item label="授权模式">
              <el-select v-model="form.authorization_mode" style="width: 100%">
                <el-option label="只建议（全部需确认）" value="suggest" />
                <el-option label="半自动（低风险自动）" value="semi_auto" />
                <el-option label="全自动（含日历草稿）" value="full_auto" />
              </el-select>
            </el-form-item>
            <el-form-item label="每日调研时间">
              <el-input-number v-model="form.schedule_hour" :min="0" :max="23" />
              <span class="hint">点（本地服务器时间）</span>
            </el-form-item>
            <el-button type="primary" :loading="running" native-type="submit" style="width: 100%">
              完整闭环：策略→调研→评分→周计划→复盘
            </el-button>
            <el-button style="width: 100%; margin-top: 8px" :loading="runningGolden" @click="runGoldenOnly">
              仅黄金流程（更快）
            </el-button>
          </el-form>
        </el-card>

        <el-card shadow="never" class="panel" style="margin-top: 16px">
          <template #header>
            <div class="card-h">已保存策略</div>
          </template>
          <div v-if="!strategies.length" class="empty">暂无策略，先跑一遍黄金流程</div>
          <div v-for="s in strategies" :key="s.id" class="strat-item" @click="selectStrategy(s)">
            <div class="strat-title">{{ s.strategy_name }} · v{{ s.version }}</div>
            <div class="strat-meta">{{ s.platform }} / {{ s.account_id }}</div>
            <div class="strat-goal">{{ s.goal_text || (s.strategy && s.strategy.positioning) || '—' }}</div>
          </div>
        </el-card>

        <el-card shadow="never" class="panel" style="margin-top: 16px">
          <template #header>
            <div class="card-h">定时任务</div>
          </template>
          <div v-if="!tasks.length" class="empty">暂无任务</div>
          <div v-for="t in tasks" :key="t.id" class="task-item">
            <div class="task-row">
              <strong>{{ t.name }}</strong>
              <el-tag size="small" :type="t.enabled ? 'success' : 'info'">{{ t.enabled ? '启用' : '停用' }}</el-tag>
            </div>
            <div class="strat-meta">
              每天 {{ String(t.schedule_hour).padStart(2, '0') }}:{{ String(t.schedule_minute).padStart(2, '0') }}
              · 下次 {{ t.next_run_at || '—' }}
            </div>
            <div class="kw">{{ (t.keywords || []).join(' · ') }}</div>
            <el-button size="small" :loading="runningTaskId === t.id" @click="runTaskNow(t)">立即执行</el-button>
          </div>
        </el-card>
      </el-col>

      <el-col :xs="24" :lg="14">
        <el-card shadow="never" class="panel" v-if="streamParts.length || streamRunning">
          <template #header>
            <div class="card-h row-between">
              <span>实时执行 · Agent 时间线</span>
              <el-button v-if="streamRunning" size="small" @click="stopStream">停止</el-button>
            </div>
          </template>
          <AgentMessageParts :parts="streamParts" :streaming="streamRunning" />
        </el-card>

        <el-card shadow="never" class="panel" :style="streamParts.length || streamRunning ? 'margin-top: 16px' : ''">
          <template #header>
            <div class="card-h">结构化结果 · 可点选推进</div>
          </template>

          <div v-if="summary" class="summary">{{ summary }}</div>
          <el-alert
            v-if="needSearchAccounts.length"
            type="warning"
            :closable="false"
            style="margin-bottom: 12px"
            :title="`缺少搜索账号：${needSearchAccounts.join(', ')}。策略与任务已保存，配置账号后可继续调研。`"
          />

          <div v-if="currentStrategy" class="block">
            <h4>策略摘要</h4>
            <p>{{ currentStrategy.positioning || currentStrategy.business_goal }}</p>
            <div class="chips">
              <el-tag v-for="p in (currentStrategy.content_pillars || [])" :key="p.name" size="small" effect="plain">
                {{ p.name }}
              </el-tag>
            </div>
          </div>

          <div v-if="topics.length" class="block">
            <h4>推荐选题</h4>
            <div v-for="t in topics" :key="t.id" class="topic-card">
              <div class="topic-title">{{ t.title }}</div>
              <div class="scores">
                适合度 {{ Math.round(t.fit_score) }} · 竞争 {{ Math.round(t.competition) }} ·
                商业 {{ Math.round(t.commercial_score) }} · 综合 {{ Math.round(t.overall_score) }}
              </div>
              <p class="muted">{{ t.pain_point || t.value_prop }}</p>
              <div class="chips">
                <el-tag
                  v-for="(d, i) in (t.differentiation_angles || []).slice(0, 3)"
                  :key="i"
                  size="small"
                  type="success"
                  effect="plain"
                >{{ d }}</el-tag>
              </div>
            </div>
          </div>

          <div v-if="scoredItems.length" class="block">
            <h4>热门内容评分 Top</h4>
            <div v-for="(it, idx) in scoredItems.slice(0, 8)" :key="it.content_id + idx" class="score-row">
              <span class="rank">{{ idx + 1 }}</span>
              <div class="grow">
                <div class="score-title">{{ it.title || '无标题' }}</div>
                <div class="muted">
                  {{ it.platform }} · 赞{{ it.likes }} 藏{{ it.collects }} 评{{ it.comments }}
                  · 综合 {{ it.scores?.overall }}
                </div>
                <div v-if="it.traffic_diagnosis?.label" class="muted">
                  流量：{{ it.traffic_diagnosis.label }}
                  · 可迁移 {{ it.traffic_diagnosis.migratable ? '是' : '否' }}
                  · {{ it.traffic_diagnosis.entry_timing }}
                </div>
                <div v-if="it.viral_breakdown?.why_works" class="muted">
                  拆解：{{ it.viral_breakdown.why_works }}
                </div>
                <div v-if="(it.differentiation || []).length" class="chips">
                  <el-tag v-for="(d, di) in it.differentiation.slice(0,3)" :key="di" size="small" type="success" effect="plain">{{ d }}</el-tag>
                </div>
              </div>
            </div>
          </div>

          <div v-if="actions.length" class="block">
            <h4>点选操作</h4>
            <p class="muted">勾选后执行：生成笔记/脚本/标题/封面，或加入发布计划</p>
            <el-checkbox-group v-model="selectedActionIds" class="action-list">
              <div v-for="a in visibleActions" :key="a.id" class="action-item">
                <el-checkbox :label="a.id" :disabled="a.status === 'executed'">
                  <span class="act-label">{{ a.label }}</span>
                  <el-tag size="small" effect="plain" style="margin-left: 8px">{{ a.status }}</el-tag>
                </el-checkbox>
                <div class="muted act-desc">{{ a.description }}</div>
              </div>
            </el-checkbox-group>
            <el-button
              type="primary"
              :disabled="!selectedActionIds.length"
              :loading="executing"
              @click="executeActions"
            >
              执行所选（{{ selectedActionIds.length }}）
            </el-button>
          </div>

          <div v-if="artifactsPreview" class="block">
            <h4>已生成产物</h4>
            <pre class="artifact">{{ artifactsPreview }}</pre>
          </div>

          <div v-if="proposals.length" class="block">
            <h4>主动建议</h4>
            <div v-for="p in proposals" :key="p.id" class="topic-card">
              <div class="topic-title">{{ p.title }}</div>
              <p class="muted">{{ p.reason }}</p>
            </div>
          </div>

          <div v-if="!summary && !topics.length && !actions.length" class="empty big">
            在左侧填写账号与目标，启动黄金流程后，这里会出现可点选的选题与后续操作。
          </div>
        </el-card>

        <el-card shadow="never" class="panel" style="margin-top: 16px">
          <template #header>
            <div class="card-h">历史运行</div>
          </template>
          <div v-for="r in runs" :key="r.id" class="run-item" @click="loadRun(r.id)">
            <div class="task-row">
              <strong>#{{ r.id }} {{ r.run_type }}</strong>
              <el-tag size="small" :type="r.status === 'completed' ? 'success' : 'warning'">{{ r.status }}</el-tag>
            </div>
            <div class="muted">{{ r.created_at }} · 选题 {{ (r.topics || []).length }} · 动作 {{ (r.actions || []).length }}</div>
          </div>
        </el-card>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { ElMessage } from 'element-plus'
import { contentOpsApi, publishV2Api } from '../api'
import { createPartsAccumulator, runAgentStream } from '../utils/agentStream'
import AgentMessageParts from '../components/agent/AgentMessageParts.vue'
import SectionHeader from '../components/shell/SectionHeader.vue'

export default {
  name: 'ContentOpsAgent',
  components: { AgentMessageParts, SectionHeader },
  setup() {
    const form = ref({
      platform: 'xiaohongshu',
      account_id: '',
      search_account_id: '',
      goal_text: '',
      keywordsText: '',
      research_platforms: ['xiaohongshu', 'douyin'],
      schedule_hour: 9,
      authorization_mode: 'suggest',
    })
    const accounts = ref([])
    const strategies = ref([])
    const tasks = ref([])
    const runs = ref([])
    const summary = ref('')
    const currentStrategy = ref(null)
    const topics = ref([])
    const scoredItems = ref([])
    const actions = ref([])
    const selectedActionIds = ref([])
    const runId = ref(null)
    const artifacts = ref({})
    const needSearchAccounts = ref([])
    const running = ref(false)
    const runningGolden = ref(false)
    const executing = ref(false)
    const loadingMeta = ref(false)
    const runningTaskId = ref(null)
    const proposals = ref([])
    const overview = ref(null)
    const streamParts = ref([])
    const streamRunning = ref(false)
    let abortCtrl = null

    const visibleActions = computed(() =>
      (actions.value || []).filter((a) =>
          [
          'select_topic', 'produce_full_package', 'generate_xhs_note', 'generate_douyin_script', 'generate_titles',
          'generate_cover_plans', 'generate_cover_images', 'create_ab_variants', 'add_to_calendar', 'enqueue_publish',
          'create_research_task', 'save_strategy', 'regenerate_diff', 'build_weekly_plan', 'run_review',
        ].includes(a.action_type)
      )
    )

    const artifactsPreview = computed(() => {
      const a = artifacts.value || {}
      const keys = ['xhs_notes', 'douyin_scripts', 'titles', 'cover_plans', 'calendar', 'selected_topics']
      const slim = {}
      keys.forEach((k) => {
        if (a[k] && a[k].length) slim[k] = a[k]
      })
      return Object.keys(slim).length ? JSON.stringify(slim, null, 2) : ''
    })

    const parseKeywords = () =>
      form.value.keywordsText
        .split(/[,，、\n]/)
        .map((s) => s.trim())
        .filter(Boolean)

    const stopStream = () => {
      if (abortCtrl) {
        abortCtrl.abort()
        abortCtrl = null
      }
      streamRunning.value = false
    }

    const applyStreamCard = (cardType, data) => {
      if (cardType === 'strategy' && data) {
        currentStrategy.value = data.strategy || data
      } else if (cardType === 'topics' && Array.isArray(data)) {
        topics.value = data
      } else if (cardType === 'scored_items' && Array.isArray(data)) {
        scoredItems.value = data
      } else if (cardType === 'actions' && Array.isArray(data)) {
        actions.value = data
      } else if (cardType === 'proposals' && Array.isArray(data)) {
        proposals.value = data
      } else if (cardType === 'need_accounts' && Array.isArray(data)) {
        needSearchAccounts.value = data
      }
    }

    const runStreamFlow = async (path, body) => {
      const acc = createPartsAccumulator()
      streamParts.value = acc.parts
      streamRunning.value = true
      abortCtrl = new AbortController()
      let finalResult = null

      try {
        await runAgentStream(
          path,
          body,
          {
            onTextDelta: (c) => acc.onTextDelta(c),
            onToolStart: (t) => acc.onToolStart(t),
            onToolComplete: (t) => acc.onToolComplete(t),
            onCard: applyStreamCard,
            onComplete: (result) => {
              finalResult = result
            },
            onError: (err) => {
              acc.onTextDelta(`\n出错：${err.message || err}`)
            },
          },
          { signal: abortCtrl.signal },
        )
        return finalResult
      } finally {
        streamRunning.value = false
        abortCtrl = null
      }
    }

    const loadAccounts = async () => {
      try {
        const res = await publishV2Api.listAccounts({ platform: form.value.platform, limit: 200 })
        const items = res.items || res.accounts || res || []
        accounts.value = Array.isArray(items) ? items : []
        if (!form.value.account_id && accounts.value.length) {
          form.value.account_id = accounts.value[0].account_id
        }
      } catch (e) {
        accounts.value = []
      }
    }

    const loadStrategies = async () => {
      const res = await contentOpsApi.listStrategies({
        platform: form.value.platform,
        account_id: form.value.account_id || undefined,
      })
      strategies.value = res.items || []
    }

    const loadTasks = async () => {
      const res = await contentOpsApi.listTasks({
        platform: form.value.platform,
        account_id: form.value.account_id || undefined,
      })
      tasks.value = res.items || []
    }

    const loadRuns = async () => {
      const res = await contentOpsApi.listRuns({
        platform: form.value.platform,
        account_id: form.value.account_id || undefined,
        limit: 15,
      })
      runs.value = res.items || []
    }

    const refreshAll = async () => {
      loadingMeta.value = true
      try {
        await loadAccounts()
        await Promise.all([loadStrategies(), loadTasks(), loadRuns()])
        try {
          overview.value = await contentOpsApi.overview({
            platform: form.value.platform,
            account_id: form.value.account_id || undefined,
          })
        } catch (_) {
          overview.value = null
        }
      } finally {
        loadingMeta.value = false
      }
    }

    const onPlatformChange = async () => {
      form.value.account_id = ''
      await refreshAll()
    }

    const applyOutput = (payload) => {
      const out = payload.output || payload
      summary.value = payload.summary || out.summary || ''
      needSearchAccounts.value = payload.need_search_accounts || []
      const strat = payload.strategy
      if (strat) {
        currentStrategy.value = strat.strategy || strat
      }
      topics.value = out.topics || payload.topics || []
      scoredItems.value = out.scored_items || payload.scored_items || []
      actions.value = out.actions || payload.actions || []
      artifacts.value = out.artifacts || payload.artifacts || {}
      runId.value = payload.run_id || out.artifacts?.run_id || payload.run?.id || null
      selectedActionIds.value = []
    }

    const runFullCycle = async () => {
      if (!form.value.account_id) {
        ElMessage.warning('请选择运营账号')
        return
      }
      if (!form.value.goal_text.trim()) {
        ElMessage.warning('请填写业务目标')
        return
      }
      running.value = true
      try {
        const res = await runStreamFlow(
          contentOpsApi.fullCycleStreamPath,
          {
            platform: form.value.platform,
            account_id: form.value.account_id,
            goal_text: form.value.goal_text.trim(),
            keywords: parseKeywords(),
            research_platforms: form.value.research_platforms,
            search_account_id: form.value.search_account_id || form.value.account_id,
            authorization_mode: form.value.authorization_mode,
            save_strategy: true,
            deepen_traffic: true,
            build_week_plan: true,
            auto_review: true,
            top_n: 10,
            topic_count: 5,
          },
        )
        if (res) {
          applyOutput(res.golden || res)
          if (res.roles?.traffic?.items) scoredItems.value = res.roles.traffic.items
          proposals.value = res.proposals || proposals.value
          summary.value = res.summary || summary.value
        }
        ElMessage.success('完整闭环已完成')
        await Promise.all([loadStrategies(), loadTasks(), loadRuns()])
      } catch (e) {
        if (e?.name !== 'AbortError') ElMessage.error(e.message || '闭环失败')
      } finally {
        running.value = false
      }
    }

    const runGoldenOnly = async () => {
      if (!form.value.account_id) {
        ElMessage.warning('请选择运营账号')
        return
      }
      if (!form.value.goal_text.trim()) {
        ElMessage.warning('请填写业务目标')
        return
      }
      runningGolden.value = true
      try {
        const res = await runStreamFlow(
          contentOpsApi.goldenFlowStreamPath,
          {
            platform: form.value.platform,
            account_id: form.value.account_id,
            goal_text: form.value.goal_text.trim(),
            keywords: parseKeywords(),
            research_platforms: form.value.research_platforms,
            search_account_id: form.value.search_account_id || form.value.account_id,
            save_strategy: true,
            create_daily_task: true,
            schedule_hour: form.value.schedule_hour,
            top_n: 10,
            topic_count: 5,
          },
        )
        if (res) applyOutput(res)
        ElMessage.success('黄金流程已完成')
        await Promise.all([loadStrategies(), loadTasks(), loadRuns()])
      } catch (e) {
        if (e?.name !== 'AbortError') ElMessage.error(e.message || '黄金流程失败')
      } finally {
        runningGolden.value = false
      }
    }

    const runGolden = runFullCycle

    const executeActions = async () => {
      if (!runId.value) {
        ElMessage.warning('没有可操作的运行记录')
        return
      }
      executing.value = true
      try {
        const res = await contentOpsApi.selectActions({
          run_id: runId.value,
          action_ids: selectedActionIds.value,
          execute: true,
          extra: { authorization_mode: form.value.authorization_mode },
        })
        if (res.run) {
          actions.value = res.run.actions || actions.value
          artifacts.value = res.artifacts || res.run.artifacts || {}
        }
        ElMessage.success('已执行所选操作')
        selectedActionIds.value = []
        await loadRuns()
      } catch (e) {
        ElMessage.error(e.message || '执行失败')
      } finally {
        executing.value = false
      }
    }

    const loadRun = async (id) => {
      try {
        const r = await contentOpsApi.getRun(id)
        runId.value = r.id
        summary.value = r.report?.opportunity || `运行 #${r.id}`
        topics.value = r.topics || []
        scoredItems.value = r.scored_items || []
        actions.value = r.actions || []
        artifacts.value = r.artifacts || {}
        selectedActionIds.value = []
      } catch (e) {
        ElMessage.error(e.message || '加载失败')
      }
    }

    const selectStrategy = (s) => {
      currentStrategy.value = s.strategy || {}
      form.value.account_id = s.account_id
      form.value.platform = s.platform
      form.value.goal_text = s.goal_text || ''
      ElMessage.info(`已载入策略 v${s.version}`)
    }

    const runTaskNow = async (t) => {
      runningTaskId.value = t.id
      try {
        const res = await contentOpsApi.runTask(t.id)
        applyOutput(res.output || res)
        ElMessage.success('任务已执行')
        await loadRuns()
      } catch (e) {
        ElMessage.error(e.message || '任务执行失败')
      } finally {
        runningTaskId.value = null
      }
    }

    onMounted(refreshAll)
    onBeforeUnmount(stopStream)

    return {
      form,
      accounts,
      strategies,
      tasks,
      runs,
      summary,
      currentStrategy,
      topics,
      scoredItems,
      actions,
      selectedActionIds,
      visibleActions,
      artifactsPreview,
      needSearchAccounts,
      running,
      runningGolden,
      executing,
      loadingMeta,
      runningTaskId,
      proposals,
      overview,
      streamParts,
      streamRunning,
      stopStream,
      refreshAll,
      onPlatformChange,
      runFullCycle,
      runGoldenOnly,
      runGolden,
      executeActions,
      loadRun,
      selectStrategy,
      runTaskNow,
    }
  },
}
</script>

<style scoped>
.ops { max-width: 1400px; }
.overview-strip {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 14px;
}
.ov-item {
  background: var(--mp-bg-soft, #f6f7f9);
  border: 1px solid var(--mp-border);
  border-radius: 8px;
  padding: 8px 12px;
  min-width: 88px;
}
.ov-item span { display:block; font-size:11px; color:var(--mp-text-muted); }
.ov-item b { font-size:16px; }
.ov-item.wide { flex: 1; min-width: 180px; }
.ov-item.wide b { font-size:12px; font-weight:600; }
.panel { border: 1px solid var(--mp-border); }
.card-h { font-weight: 700; font-size: 13px; }
.row-between { display: flex; justify-content: space-between; align-items: center; gap: 8px; }
.hint { margin-left: 8px; color: var(--mp-text-muted); font-size: 12px; }
.empty { color: var(--mp-text-muted); font-size: 12px; padding: 8px 0; }
.empty.big { padding: 48px 12px; text-align: center; }
.summary {
  background: var(--mp-bg-soft, #f6f7f9);
  border-radius: 8px;
  padding: 12px 14px;
  font-size: 13px;
  line-height: 1.6;
  margin-bottom: 14px;
}
.block { margin-bottom: 18px; }
.block h4 { margin: 0 0 10px; font-size: 13px; }
.topic-card, .strat-item, .task-item, .run-item {
  border: 1px solid var(--mp-border);
  border-radius: 8px;
  padding: 10px 12px;
  margin-bottom: 8px;
}
.strat-item, .run-item { cursor: pointer; }
.strat-item:hover, .run-item:hover { border-color: var(--el-color-primary); }
.topic-title, .strat-title, .score-title { font-weight: 650; font-size: 13px; }
.scores, .strat-meta, .muted, .kw { color: var(--mp-text-muted); font-size: 12px; margin-top: 4px; }
.chips { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 8px; }
.score-row { display: flex; gap: 10px; align-items: flex-start; margin-bottom: 8px; }
.rank {
  width: 22px; height: 22px; border-radius: 6px;
  background: var(--mp-bg-soft, #f0f1f3);
  display: flex; align-items: center; justify-content: center;
  font-size: 11px; font-weight: 700;
}
.grow { flex: 1; min-width: 0; }
.task-row { display: flex; justify-content: space-between; align-items: center; gap: 8px; }
.action-list { display: flex; flex-direction: column; gap: 8px; width: 100%; margin-bottom: 12px; }
.action-item { padding: 8px 10px; border: 1px solid var(--mp-border); border-radius: 8px; }
.act-label { font-size: 13px; font-weight: 600; }
.act-desc { margin-left: 24px; }
.artifact {
  max-height: 320px;
  overflow: auto;
  font-size: 11px;
  background: #0f1218;
  color: #d7dde8;
  padding: 12px;
  border-radius: 8px;
}
</style>
